// White-label branding for a dealer's portal. Once a dealer uploads a logo in
// Settings (users/{uid}.brandLogoUrl), their logo and business name replace
// GharSetu's in the rail, the loading screen and the browser tab, with a
// "Powered by GharSetu" line kept under the name.

const BRAND_CACHE_KEY = "gs-brand";
const LOGO_MAX_EDGE = 512;
const LOGO_MIN_EDGE = 64;

export const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];
const LOGO_MAX_BYTES = 10 * 1024 * 1024;

const DEFAULT_FAVICON =
  document.querySelector("link[rel='icon']")?.getAttribute("href") || "/favicon.ico";

// The signed-in dealer's brand on this device, so the loading screen can show
// their logo before the session is confirmed. Removed whenever the session
// ends (App.js), so a signed-out device falls back to GharSetu.
export function readCachedBrand() {
  try {
    const brand = JSON.parse(localStorage.getItem(BRAND_CACHE_KEY) || "null");
    return brand && typeof brand.logoUrl === "string" ? brand : null;
  } catch {
    return null;
  }
}

export function cacheBrand(brand) {
  try {
    if (brand?.logoUrl) {
      localStorage.setItem(
        BRAND_CACHE_KEY,
        JSON.stringify({ logoUrl: brand.logoUrl, name: brand.name || "", wide: !!brand.wide })
      );
    } else {
      localStorage.removeItem(BRAND_CACHE_KEY);
    }
  } catch {
    // Storage blocked: the loading screen shows the GharSetu logo instead.
  }
}

/** Sets the browser tab's title and icon; no icon restores GharSetu's. */
export function applyTabBrand(title, iconUrl) {
  if (document.title !== title) document.title = title;
  let icon = document.querySelector("link[rel='icon']");
  if (!icon) {
    icon = document.createElement("link");
    icon.rel = "icon";
    document.head.appendChild(icon);
  }
  const href = iconUrl || DEFAULT_FAVICON;
  if (icon.getAttribute("href") !== href) icon.setAttribute("href", href);
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Checks a picked logo and re-encodes it as a PNG no larger than 512 px on
 * its long edge, keeping transparency. Resolves to { blob } or to { error }
 * with a message for the dealer.
 */
export async function prepareLogo(file) {
  if (!LOGO_TYPES.includes(file.type)) {
    return { error: "Use a PNG, JPG or WEBP image." };
  }
  if (file.size > LOGO_MAX_BYTES) {
    return { error: "That image is over 10 MB. Use a smaller file." };
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const longEdge = Math.max(img.naturalWidth, img.naturalHeight);
    if (longEdge < LOGO_MIN_EDGE) {
      return {
        error: `That image is only ${img.naturalWidth} × ${img.naturalHeight} px. Use a logo at least 256 px wide so it stays sharp.`,
      };
    }

    const scale = Math.min(1, LOGO_MAX_EDGE / longEdge);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    return blob ? { blob } : { error: "Couldn't read that image. Try another file." };
  } catch {
    return { error: "Couldn't read that image. Try another file." };
  } finally {
    URL.revokeObjectURL(url);
  }
}
